import { LoadingState } from '@/components/ui/async-state';
import { AppScreen } from '@/components/ui/scene';
import { EventEditor } from '@/features/events/editor/EventEditor';
import { useAuth } from '@/lib/auth/auth';
import { useAccountContext } from '@/features/workspaces/useAccountContext';
import { useVenueDetail } from '@/features/venues/useVenueDetail';
import { Stack, useLocalSearchParams } from 'expo-router';
import * as React from 'react';

export default function CreateEventScreen() {
  const session = useAuth((state) => state.session);
  const isHydrating = useAuth((state) => state.isHydrating);
  const { currentContext, currentBusinessMembership } = useAccountContext();
  const params = useLocalSearchParams<{
    venueId?: string;
    venueName?: string;
    address?: string;
    latitude?: string;
    longitude?: string;
  }>();
  const ownedVenueID =
    session?.me.profile.is_venue ? session.me.profile.venue_id ?? null : null;
  const businessVenueID =
    currentContext?.kind === 'business' ? currentContext.activeVenueId : null;
  const defaultVenueID = params.venueId ?? businessVenueID ?? ownedVenueID ?? null;
  const shouldLoadDefaultVenue = Boolean(defaultVenueID);
  const ownedVenueDetailQuery = useVenueDetail(shouldLoadDefaultVenue ? defaultVenueID : null);
  const ownedVenueDetail = ownedVenueDetailQuery.data;

  const createPrefill = React.useMemo(
    () => ({
      venueId: defaultVenueID,
      venueName:
        params.venueName ??
        ownedVenueDetail?.name ??
        (currentContext?.kind === 'business'
          ? currentBusinessMembership?.venues.find((venue) => venue.venue_id === defaultVenueID)?.venue_name
          : undefined) ??
        session?.me.profile.venue_name ??
        undefined,
      address: params.address ?? ownedVenueDetail?.address ?? undefined,
      latitude: params.latitude
        ? Number(params.latitude)
        : ownedVenueDetail?.lat ?? undefined,
      longitude: params.longitude
        ? Number(params.longitude)
        : ownedVenueDetail?.lng ?? undefined,
    }),
    [
      ownedVenueDetail?.address,
      ownedVenueDetail?.lat,
      ownedVenueDetail?.lng,
      ownedVenueDetail?.name,
      currentBusinessMembership?.venues,
      currentContext?.kind,
      defaultVenueID,
      params.address,
      params.latitude,
      params.longitude,
      params.venueName,
      session?.me.profile.venue_name,
    ]
  );

  if (isHydrating || (shouldLoadDefaultVenue && ownedVenueDetailQuery.isPending)) {
    return (
      <>
        <Stack.Screen options={{ headerShown: false }} />
        <AppScreen>
          <LoadingState label="Loading event composer..." />
        </AppScreen>
      </>
    );
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <EventEditor mode="create" createPrefill={createPrefill} />
    </>
  );
}
